// Journey Configuration for Frictionless Signup
// This file defines the different signup journeys (CICD, DBT, etc.)

export interface JourneyConfig {
  app_name: string;
  app_variant: string;
  redirect_to: string;
  title: string;
  description: string;
  backgroundImage?: string;
  primaryColor?: string;
}

export const JOURNEY_CONFIGS: Record<string, JourneyConfig> = {
  cicd: {
    app_name: 'Automated CI/CD',
    app_variant: 'cicd',
    redirect_to: '/',
    title: 'Get started with DataOps CICD',
    description: 'Automate your data pipeline deployments with continuous integration and delivery',
    backgroundImage: 'abstract_illustration_6',
    primaryColor: '#0066CC',
  },
  dbt: {
    app_name: 'Automated DBT Migration',
    app_variant: 'dbt',
    redirect_to: '/',
    title: 'Get started with DBT Transformation',
    description: 'Migrate and manage your DBT projects with powerful automation',
    backgroundImage: 'abstract_illustration_18',
    primaryColor: '#FF6B35',
  },
  default: {
    app_name: 'Onboarding',
    app_variant: 'default',
    redirect_to: '/',
    title: 'Get started with DataOps.live',
    description: 'Create your account and start automating your data pipelines',
  },
};

// Default journey if none specified or if 404 from status endpoint
export const DEFAULT_JOURNEY = 'default';

/**
 * Helper function to get journey configuration from URL query parameters
 * @param searchParams - URLSearchParams from useSearchParams hook
 * @returns JourneyConfig for the specified journey or default journey
 *
 * @example
 * const [searchParams] = useSearchParams();
 * const journey = getJourneyFromQueryParam(searchParams);
 */
export function getJourneyFromQueryParam(searchParams: URLSearchParams): JourneyConfig {
  const appParam = searchParams.get('app');
  if (appParam != null && appParam !== '' && appParam in JOURNEY_CONFIGS) {
    return JOURNEY_CONFIGS[appParam];
  }
  return JOURNEY_CONFIGS[DEFAULT_JOURNEY];
}

/**
 * Helper function to check if a journey variant exists
 * @param variant - The journey variant to check
 * @returns boolean indicating if the variant exists
 */
export function isValidJourney(variant: string): boolean {
  return variant in JOURNEY_CONFIGS;
}

/**
 * Helper function to get all available journey variants
 * @returns Array of journey variant keys
 */
export function getAvailableJourneys(): string[] {
  return Object.keys(JOURNEY_CONFIGS);
}

/**
 * HOW TO ADD A NEW JOURNEY:
 *
 * 1. Add a new entry to JOURNEY_CONFIGS with the following structure:
 *    {
 *      app_name: 'Display name for the application',
 *      app_variant: 'unique-identifier',
 *      redirect_to: '/path-after-signup',
 *      title: 'Page title for signup',
 *      description: 'Description shown on signup page',
 *      backgroundImage: '/path/to/image.jpg', // optional
 *      primaryColor: '#HEX', // optional
 *    }
 *
 * 2. Users can then access this journey via: /signup?app=unique-identifier
 *
 * 3. The journey configuration will be used throughout the signup flow to:
 *    - Customize the signup page branding
 *    - Pass app_name and app_variant to the backend API
 *    - Redirect users after successful signup
 *
 * Example:
 *
 * analytics: {
 *   app_name: 'Data Analytics',
 *   app_variant: 'analytics',
 *   redirect_to: '/analytics-dashboard',
 *   title: 'Get started with Data Analytics',
 *   description: 'Transform your data into actionable insights',
 *   primaryColor: '#4CAF50',
 * }
 */
